/**
 * Registration metadata for the sidebar "Music theory" group.
 *
 * This array is the single source of truth for the group's membership, order AND sub-grouping,
 * authored top-to-bottom in Bowie's pedagogical order (Overview first, then the prerequisite
 * chain, then the trainers). Reorder or regroup here, not in the sidebar component: the sidebar
 * renders one sub-labelled block per group, in the order groups first appear.
 *
 * The flat tool rows (Mixer, Tuner, Keyboard chords, Guitar chords, Guitar patterns) are
 * intentionally NOT in this list. They stay ungrouped.
 */
export const musicTheoryGroups = ['Foundations', 'Labs', 'Training'] as const
export type MusicTheoryGroup = (typeof musicTheoryGroups)[number]

export interface MusicTheoryTool {
  /** Sidebar label, sentence case. "Overview" is the hub page relabeled for this context only. */
  label: string
  /** App route the row navigates to. */
  route: string
  /** The sub-labelled block the row renders under. */
  group: MusicTheoryGroup
}

export const musicTheoryTools: MusicTheoryTool[] = [
  { label: 'Overview', route: '/app/theory', group: 'Foundations' },
  { label: 'Circle of fifths', route: '/app/circle', group: 'Foundations' },
  { label: 'Function quiz', route: '/app/quiz', group: 'Foundations' },
  { label: 'Cadence lab', route: '/app/cadences', group: 'Labs' },
  { label: 'Mode lab', route: '/app/modes', group: 'Labs' },
  { label: 'Reharm lab', route: '/app/reharm', group: 'Labs' },
  { label: 'Voice-leading lab', route: '/app/voice-leading', group: 'Labs' },
  { label: 'Modulation lab', route: '/app/modulate', group: 'Labs' },
  { label: 'Form visualizer', route: '/app/form', group: 'Labs' },
  { label: 'Melody writer', route: '/app/melody', group: 'Labs' },
  { label: 'Ear training', route: '/app/ear-training', group: 'Training' },
  { label: 'Pitch training', route: '/app/pitch-training', group: 'Training' },
  { label: 'Melody training', route: '/app/melody-trainer', group: 'Training' },
  { label: 'Rhythm training', route: '/app/rhythm', group: 'Training' },
]

export interface MusicTheoryToolGroup {
  group: MusicTheoryGroup
  tools: MusicTheoryTool[]
}

/** Group the tools for rendering: groups in first-seen order, tools in authored order within each. */
export const groupMusicTheoryTools = (tools: MusicTheoryTool[]): MusicTheoryToolGroup[] =>
  tools.reduce<MusicTheoryToolGroup[]>((groups, tool) => {
    const existing = groups.find(g => g.group === tool.group)
    if (existing) {
      existing.tools.push(tool)
      return groups
    }
    return [...groups, { group: tool.group, tools: [tool] }]
  }, [])
